import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services/firebaseFunctions';
import { awaitAuthReady } from '@/services/auth/awaitAuthReady';
import {
    FetchRecordsListParams,
    FetchRecordsListResponse
} from '#types/System.RecordsRegistry.types';
import type { EntityRecord } from '#types/System.types';

/**
 * Fetches entity record data from the listRecords cloud function.
 * 
 * This service provides lightweight record metadata for populating EntityRecordUI
 * dropdowns without the overhead of full record data. Optionally, it can return
 * full record data or specific properties when needed.
 * 
 * **Use Cases:**
 * - Initial registry cache population (metadata only)
 * - On-demand loading of specific entity records
 * - Registering common record usage tracking
 * - Fetching full record data or specific properties
 * 
 * **Performance Considerations:**
 * - Returns only essential metadata by default (minimal payload)
 * - Batches usage tracking to minimize writes
 * - Combines recent + commonly used records
 * - Use includeRecordData to control payload size and client memory usage
 * 
 * @param params - Configuration for record fetching
 * @param params.entityId - Entity ID to fetch records from (required)
 * @param params.limit - Maximum number of recent records (default: 1000)
 * @param params.commonRecordIds - Specific record IDs to include
 * @param params.entityGroupId - Filter by entity group ID
 * @param params.registerUsage - Record IDs to track usage for (fire-and-forget)
 * @param params.includeRecordData - When true, include all record data. When object, include only specified properties (keys matter, values ignored)
 * 
 * @returns Promise resolving to array of EntityRecord with requested fields populated
 * @throws Error if the cloud function call fails
 * 
 * @example
 * ```typescript
 * // Fetch recent contacts (metadata only - minimal payload)
 * const contacts = await fetchRecordsList({
 *   entityId: 'contacts',
 *   limit: 500
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // Fetch with full record data
 * const fullContacts = await fetchRecordsList({
 *   entityId: 'contacts',
 *   includeRecordData: true
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // Fetch with specific properties (optimize bandwidth & client memory)
 * const contactsWithFields = await fetchRecordsList({
 *   entityId: 'contacts',
 *   includeRecordData: {
 *     fields: {},
 *     entityReferences: {}
 *   }
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // Register usage (fire-and-forget)
 * fetchRecordsList({
 *   entityId: 'contacts',
 *   registerUsage: ['contact123', 'contact456']
 * }).catch(err => console.warn('Usage tracking failed:', err));
 * ```
 */
export const fetchRecordsList = async (
    params: FetchRecordsListParams
): Promise<EntityRecord[]> => {
    await awaitAuthReady();
    const listRecordsCallable = httpsCallable<FetchRecordsListParams, FetchRecordsListResponse>(
        functions,
        'listRecords'
    );

    try {
        const result = await listRecordsCallable(params);
        return result.data.records;
    } catch (error) {
        console.error('Error fetching records list:', error);
        if (error instanceof Error) {
            throw error;
        }
        throw new Error(`Failed to fetch records: ${String(error)}`);
    }
};

